import { useEffect, useMemo, useRef } from 'react';
import type { UseFavoriteActionReturnType } from '~/hooks/use-favorite-action';
import type {
  PopupMenuFavoriteSource,
  PopupMenuFavoriteState,
  PopupMenuFavoriteStateListener,
} from './types';

/**
 * Exposes a cell's Favourite state to the Popup Menu Host as a stable,
 * subscribable source, so the hosted menu keeps its favourites label live
 * and toggles through the cell's current favourite action.
 *
 * TODO: replace with FavoriteController once it lands on main — the host
 * reads `useFavoriteState(entry.id)` and `toggleFavorite` resolves the entry
 * through `options.entryId`, so cells no longer pass a favourite source.
 * @param favoriteActionData - the cell's favourite action data
 * @returns a source whose identity never changes
 */
export function usePopupMenuFavoriteSource({
  isFavorite,
  shouldRenderFavoriteAction,
  favoriteAction,
}: UseFavoriteActionReturnType): PopupMenuFavoriteSource {
  const stateRef = useRef<PopupMenuFavoriteState>({
    isFavorite,
    isAvailable: shouldRenderFavoriteAction,
  });
  const favoriteActionRef = useRef<() => void>(favoriteAction);
  const listenersRef = useRef<Set<PopupMenuFavoriteStateListener>>(new Set());

  useEffect(() => {
    favoriteActionRef.current = favoriteAction;
  }, [favoriteAction]);

  useEffect(() => {
    const currentState: PopupMenuFavoriteState = stateRef.current;

    if (
      currentState.isFavorite === isFavorite &&
      currentState.isAvailable === shouldRenderFavoriteAction
    ) {
      return;
    }

    stateRef.current = { isFavorite, isAvailable: shouldRenderFavoriteAction };
    listenersRef.current.forEach((listener: PopupMenuFavoriteStateListener) =>
      listener()
    );
  }, [isFavorite, shouldRenderFavoriteAction]);

  return useMemo(
    (): PopupMenuFavoriteSource => ({
      getState: (): PopupMenuFavoriteState => stateRef.current,
      subscribe: (listener: PopupMenuFavoriteStateListener): (() => void) => {
        listenersRef.current.add(listener);

        return (): void => {
          listenersRef.current.delete(listener);
        };
      },
      toggle: (): void => favoriteActionRef.current(),
    }),
    []
  );
}
